import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, Lightning, X, Clock } from "@phosphor-icons/react";

const CCYS = ["USD", "EUR", "GBP", "AED", "INR", "JPY", "CHF", "SGD"];

export default function FixedDepositsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [accts, setAccts] = useState([]);
  const [form, setForm] = useState({
    counterparty: "", principal: "", currency: "USD",
    interest_rate: "", start_date: new Date().toISOString().slice(0, 10),
    maturity_date: "", bank_account_id: "", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/fixed-deposits"), api.get("/bank-accounts")]);
    setRows(a.data); setAccts(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      principal: parseFloat(form.principal),
      interest_rate: parseFloat(form.interest_rate),
      bank_account_id: form.bank_account_id || null,
    };
    await api.post("/fixed-deposits", payload);
    setOpen(false);
    setForm({ ...form, counterparty: "", principal: "", interest_rate: "", maturity_date: "", notes: "" });
    load();
  };

  const accrue = async (id) => { await api.post(`/fixed-deposits/${id}/accrue`); load(); };
  const remove = async (id) => { await api.delete(`/fixed-deposits/${id}`); load(); };
  const runMaturity = async () => { const { data } = await api.post("/maturity/run"); alert(`${data.posted} maturity transactions posted.`); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Instruments"
        title="Fixed Deposits"
        subtitle="Place and monitor term deposits across counterparties and currencies."
        testid="fd-title"
        actions={
          <>
            <button onClick={runMaturity} data-testid="run-maturity-fd" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2">
              <Clock size={14} weight="bold" /> Run Maturity
            </button>
            <button onClick={() => setOpen(true)} data-testid="new-fd-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
              <Plus size={14} weight="bold" /> New FD
            </button>
          </>
        }
      />

      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="fd-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["ID", "Counterparty", "Principal", "Ccy", "Rate %", "Start", "Maturity", "Accrued Int.", "Status", ""].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${["Principal","Rate %","Accrued Int."].includes(h) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{r.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 font-semibold">{r.counterparty}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.principal)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.currency}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.interest_rate.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.start_date)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.maturity_date)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.accrued_interest)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${r.status === "matured" ? "text-[#DC2626]" : "text-gray-900"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => accrue(r.id)} title="Post accrual" data-testid={`accrue-fd-${r.id}`} className="text-[#0020C2] hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3">
                      <Lightning size={12} weight="bold" /> Accrue
                    </button>
                    <button onClick={() => remove(r.id)} data-testid={`del-fd-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold inline-flex items-center gap-1">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-500">No fixed deposits. Click <strong>New FD</strong> to create one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="fd-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Fixed Deposit</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["counterparty", "Counterparty / Bank", "text"],
                ["principal", "Principal", "number"],
                ["interest_rate", "Interest Rate (% annual)", "number"],
                ["start_date", "Start Date", "date"],
                ["maturity_date", "Maturity Date", "date"],
                ["notes", "Notes", "text"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={k !== "notes"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`fd-${k}`}
                    className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="fd-currency" className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]">
                    {CCYS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Bank Account</label>
                  <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} data-testid="fd-bank" className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]">
                    <option value="">— None —</option>
                    {accts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="fd-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Place deposit</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
